import Swal from 'sweetalert2';

/** Agrega fila de pregunta si es meta. */
export function pushGoalRow(all: unknown[], cat: { name: unknown; index?: number }, catNum: number, q: { index?: number; goalType: unknown }): void {
  if (isGoalKind(q.goalType) === false) return;
  all.push(makeGoalRow(cat, catNum, q));
}

/** Construye fila de pregunta. */
export function makeGoalRow(cat: { name: unknown; index?: number }, catNum: number, q: { index?: number; goalType: unknown }): Record<string, unknown> {
  const qNum = indexValue(q) + 1;
  const rowBase = { ...baseRow(cat), sortQ: indexValue(q) };
  return { ...q, ...rowBase, numberLabel: questionLabel(catNum, qNum), sortSub: -1, isPersonal: isPersonalKind(q.goalType) };
}

/** Agrega subfilas de pregunta. */
export function pushSubRows(all: unknown[], cat: { name: unknown; index?: number }, catNum: number, q: { index?: number; title: unknown; subitems?: unknown[]; goalType: unknown }): void {
  const subs = asArray(q.subitems);
  for (const sub of subs) pushSubRow(all, cat, catNum, q, sub);
}

/** Agrega una subfila si es meta. */
export function pushSubRow(all: unknown[], cat: { name: unknown; index?: number }, catNum: number, q: { index?: number; title: unknown }, sub: unknown): void {
  if (isGoalKind((sub as { goalType: unknown }).goalType) === false) return;
  all.push(makeSubRow(cat, catNum, q, sub as { index?: number; goalType: unknown }));
}

/** Construye fila de subpregunta. */
export function makeSubRow(cat: { name: unknown; index?: number }, catNum: number, q: { index?: number; title: unknown }, sub: { index?: number; goalType: unknown }): Record<string, unknown> {
  const qNum = indexValue(q) + 1;
  const rowBase = { ...baseRow(cat), sortQ: indexValue(q) };
  return { ...sub, ...rowBase, numberLabel: subLabel(catNum, qNum, indexValue(sub)), sortSub: indexValue(sub), parentTitle: q.title, isPersonal: isPersonalKind(sub.goalType) };
}

/** Toma primera entidad o nulo. */
export function firstEntity(list: unknown[]): unknown {
  if (list.length === 0) return null;
  return list[0];
}

/** Ordena filas de metas. */
export function sortGoalRows(rows: Array<{ sortCat: number; sortQ: number; sortSub: number }>): void {
  rows.sort(compareGoalRows);
}

/** Compara filas de metas. */
export function compareGoalRows(a: { sortCat: number; sortQ: number; sortSub: number }, b: { sortCat: number; sortQ: number; sortSub: number }): number {
  return rowDiff(a, b) || subDiff(a, b) || leafDiff(a, b);
}

/** Compara categoría y pregunta. */
export function rowDiff(a: { sortCat: number; sortQ: number }, b: { sortCat: number; sortQ: number }): number {
  return diffCat(a, b) || diffQuestion(a, b);
}

/** Diferencia de categoría. */
export function diffCat(a: { sortCat: number }, b: { sortCat: number }): number {
  return a.sortCat - b.sortCat;
}

/** Diferencia de pregunta. */
export function diffQuestion(a: { sortQ: number }, b: { sortQ: number }): number {
  return a.sortQ - b.sortQ;
}

/** Diferencia de subpregunta. */
export function subDiff(a: { sortSub: number }, b: { sortSub: number }): number {
  return leafDiff(a, b);
}

/** Diferencia hoja. */
export function leafDiff(a: { sortSub: number }, b: { sortSub: number }): number {
  return a.sortSub - b.sortSub;
}

/** Crea estado de pendientes. */
export function pendingState(total: number): { left: number } {
  return { left: total };
}

/** Descuenta un pendiente. */
export function takePending(state: { left: number }): void {
  state.left = state.left - 1;
}

/** Indica si terminó la carga. */
export function isPendingDone(state: { left: number }): boolean {
  return state.left === 0;
}

/** Indica si hay contexto de módulo. */
export function canLoadGoals(module: unknown, group: unknown): boolean {
  return Boolean(module) && Boolean(group);
}

/** Construye lista de años descendente. */
export function buildYearList(current: number, min: number): number[] {
  const years: number[] = [];
  pushYears(years, current, min);
  return years;
}

/** Agrega años al arreglo. */
export function pushYears(years: number[], current: number, min: number): void {
  for (let i = current; i >= min; i--) years.push(i);
}

/** Ordena categorías por índice. */
export function sortCategories(list: Array<{ index?: number }>): Array<{ index?: number }> {
  return [...list].sort((a, b) => compareIndex(a, b));
}

/** Compara índices de categoría. */
export function compareIndex(a: { index?: number }, b: { index?: number }): number {
  return indexValue(a) - indexValue(b);
}

/** Normaliza índice a número. */
export function indexValue(row: { index?: number }): number {
  return row.index ?? 0;
}

/** Normaliza arreglo. */
export function asArray(value: any): any[] {
  if (value) return value as any[];
  return [];
}

/** Indica si es fila de meta. */
export function isGoalKind(goalType: unknown): boolean {
  return goalType === 'personal' || goalType === 'general';
}

/** Indica si es meta personal. */
export function isPersonalKind(goalType: unknown): boolean {
  return goalType === 'personal';
}

/** Arma etiqueta de número. */
export function questionLabel(catNum: number, qNum: number): string {
  return `${catNum}.${qNum}`;
}

/** Arma etiqueta de subpregunta. */
export function subLabel(catNum: number, qNum: number, subIdx: number): string {
  return `${catNum}.${qNum}.${subIdx + 1}`;
}

/** Construye fila base por categoría. */
export function baseRow(cat: { name: unknown; index?: number }): Record<string, unknown> {
  return { categoryName: cat.name, categoryIndex: cat.index, sortCat: indexValue(cat) };
}

/** Construye mapa de metas por pregunta. */
export function buildGoalMap(goals: any[]): Record<string, unknown> {
  const map: Record<string, unknown> = {};
  for (const goal of goals) map[goal.questionId] = goal;
  return map;
}

/** Aplica metas a preguntas. */
export function applyGoalTargets(questions: Array<{ id: string; targetValue: unknown }>, map: Record<string, { targetValue?: unknown }>): void {
  for (const q of questions) q.targetValue = readTarget(map, q.id);
}

/** Lee valor objetivo del mapa. */
export function readTarget(map: Record<string, { targetValue?: unknown }>, id: string): unknown {
  const row = map[id];
  if (row) return row.targetValue ?? null;
  return null;
}

/** Toma id de módulo. */
export function moduleIdOf(module: { id?: string } | null): string | undefined {
  if (module) return module.id ?? undefined;
  return undefined;
}

/** Toma id de entidad. */
export function entityIdOf(entity: { id?: string } | null): string | null {
  if (entity) return entity.id ?? null;
  return null;
}

/** Construye payload de meta. */
export function buildGoalPayload(existing: { id?: string } | undefined, questionId: string, entityId: string, moduleId: string, year: number, target: number): Record<string, unknown> {
  return { id: existing?.id, questionId, entityId, moduleId, year, targetValue: target };
}

/** Indica si el valor de meta es válido. */
export function hasTargetValue(value: unknown): boolean {
  if (value === 0) return true;
  return Boolean(value);
}

/** Extrae texto de error de API. */
export function goalErrorText(err: { error?: { error?: unknown } }, fallback: string): string {
  return String(err.error?.error ?? fallback);
}

/** Avisa selección de entidad. */
export function warnSelectEntity(): void {
  Swal.fire({ icon: 'warning', title: 'Seleccione una entidad', text: 'Debe seleccionar una entidad para fijar la meta personal.' });
}

/** Avisa valor requerido. */
export function warnTargetRequired(): void {
  Swal.fire({ icon: 'warning', title: 'Valor requerido', text: 'Ingrese el valor de la meta.' });
}

/** Confirma meta guardada. */
export function toastGoalSaved(): void {
  Swal.fire({ icon: 'success', title: 'Meta guardada', timer: 1500, showConfirmButton: false });
}

/** Muestra error con texto dado. */
export function showGoalError(text: string): void {
  Swal.fire({ icon: 'error', title: 'Error', text });
}

/** Construye parámetros de historial. */
export function buildHistoryParams(questionId: string, personal: boolean, entityId: string | null, moduleId: string | undefined, year: number): Record<string, unknown> {
  return { questionId, entityId: historyEntity(personal, entityId), moduleId, year };
}

/** Resuelve entidad para historial. */
export function historyEntity(personal: boolean, entityId: string | null): string | undefined {
  if (personal === true) return entityId ?? undefined;
  return undefined;
}
